import Row from 'react-bootstrap/Row';
import { Alert } from 'react-bootstrap';
import { useField } from 'formik';
import { PatternFormat } from 'react-number-format';
import { OrganisationAndContactProps } from './types';
import ContactDetailsInput from '../../components/forms/CommonForms/ContactDetails';
import HidableField from '../../components/forms/HidableField';
import RadioButtonGroup from '../../components/Inputs/RadioButtonGroup';

const GetFieldValue = ({ fieldName }: { fieldName: string }) => {
    const [_field] = useField(fieldName);
    return (
        <>
            {_field.value}
        </>
    );
};

const OrganisationAndContact = (props: OrganisationAndContactProps) => {
    const { isSummary, name } = props;
    const [sourceReferenceField] = useField('sourceReferenceId');

    function getName(localName: string) {
        return name ? `${name}.${localName}` : localName;
    }

    return (
        <>
            {!isSummary
                ? (
                    <Alert
                        variant='info'
                        data-testid='info-summary'
                        role='status'
                        aria-live='polite'
                        className='d-flex mb-4'
                    >
                        <div className='d-flex justify-content-center justify-content-md-start mb-3 mb-md-0'>
                            <div className='bgCircle mb-3 me-3'>
                                <i className='icon-info' aria-hidden='true' />
                            </div>
                        </div>
                        <div>
                            <p className='mb-0'>
                                <strong>Important information</strong>
                            </p>
                            <p>
                                The Entity, Business, Branch and/or location name can be used to help you group your NMI Service portal requests for simplified management.
                            </p>
                            <p>
                                If applicable, please ensure you are using the correct organisation below. You can change the organisation by returning to the
                                Dashboard &gt; &quot;Add or manage branch/location&quot; and select the default organisation to manage.
                            </p>
                            {!!(sourceReferenceField.value) && (
                                <p>
                                    This &quot;Recalibration Request&quot; is prefilled based on the previous original quote request. Please review and update if any
                                    changes are required prior to submitting the new request.
                                </p>
                            )}
                        </div>
                    </Alert>
                ) : null}
            <Row className='mb-4'>
                <h2 className={isSummary ? 'h3 my-3' : ''}>
                    Organisation
                    { !isSummary ? ' details' : '' }
                </h2>
            </Row>
            <Row className='mb-4'>
                <h2 className={!isSummary ? 'h2 text-body' : 'h3'} aria-describedby='orgABN'>
                    {!isSummary && (
                        <span className='d-block mb-1 fs-5 fw-normal'>
                            {'Currently managing '}
                        </span>
                    )}
                    <span className='d-block text-break'><GetFieldValue fieldName={getName('name')} /></span>
                    <span className='d-block text-break'>
                        <GetFieldValue fieldName={getName('businessOrTradingName')} />
                        {GetFieldValue({ fieldName: getName('branchOrLocationName') }).props.children && (
                            <>
                                {' - '}
                                <GetFieldValue fieldName={getName('branchOrLocationName')} />
                            </>
                        )}
                    </span>
                    <span id='orgABN' className='d-inline-block me-3 fs-5 fw-normal'>
                        {'ABN: '}
                        <PatternFormat
                            value={GetFieldValue({ fieldName: getName('abn') }).props?.children}
                            displayType='text'
                            format='## ### ### ###'
                            aria-hidden='true'
                            role='presentation'
                            valueIsNumericString={false}
                        />
                    </span>
                    <span className='visually-hidden'>
                        {GetFieldValue({ fieldName: getName('abn') }).props?.children?.split('').join(' ')}
                    </span>
                </h2>
            </Row>
            <Row className='mb-4'>
                <h2 className={isSummary ? 'h3 my-3' : ''}>
                    Contact
                    { !isSummary ? ' information' : '' }
                </h2>
                <RadioButtonGroup
                    legend='Who is the main contact person for this request?'
                    name={getName('isPrincipalContact')}
                    id='q-isPrincipalContact'
                    isSummary={isSummary}
                    options={[
                        {
                            label: 'The stored contact person',
                            descriptor: 'To update your stored contact go to User menu > Manage contact information',
                            value: 'Yes',
                            id: 'isPrincipalContact-Yes',
                        },
                        {
                            label: 'A different contact person',
                            descriptor: 'Enter the contact details of another person. Please ensure this person has access to the NMI Services portal for the organisation to progress the request.',
                            value: 'No',
                            id: 'isPrincipalContact-No',
                        }]}
                />
                <HidableField name={getName('principalContactHide')}>
                    <ContactDetailsInput
                        key='contact'
                        name={getName('contact')}
                        isSummary={isSummary}
                    />
                </HidableField>
            </Row>
        </>
    );
};

export default OrganisationAndContact;
